// App.js
import React, { useEffect, useMemo, useState } from 'react';
import { ActivityIndicator, Platform, View } from 'react-native';
import { NavigationContainer } from '@react-navigation/native';
import { createStackNavigator } from '@react-navigation/stack';
import { SafeAreaProvider } from 'react-native-safe-area-context';

import LoginScreen from './screens/LoginScreen';
import DashboardScreen from './screens/DashboardScreen';
import AddPesticide from './screens/AddPesticide';
import PesticideList from './screens/PesticideList';
import HistoryScreen from './screens/HistoryScreen';
import ExpensesList from './screens/ExpensesList';
import AddExpense from './screens/AddExpense';
import ReceiptScanScreen from './screens/ReceiptScanScreen';
import ScannerScreen from './screens/ScannerScreen';
import InventoryScreen from './screens/InventoryScreen';
import OperationsScreen from './screens/OperationsScreen'; // 👈 TAMBAHAN
import HarvestsScreen from './screens/HarvestsScreen'; // 👈 TAMBAHAN
import ReportScreen from './screens/ReportScreen'; // 👈 TAMBAHAN
import MonitoringScreen from './screens/MonitoringScreen';
import BukuSawahScreen from './screens/BukuSawahScreen';
import TractorDashboardScreen from './screens/TractorDashboardScreen';
import LandMeasureScreen from './screens/LandMeasureScreen';
import { authStorage } from './utils/authStorage';
import { PADIPRO } from './components/padiproTheme';
import { useResponsive, NARROW_MAX_WIDTH } from './components/responsive';

// A login form stays a centered narrow card on any screen size — that's
// the normal desktop pattern too, so AuthStack never goes wide.
const authCardStyle = {
  flex: 1,
  backgroundColor: PADIPRO.bg,
  width: '100%',
  maxWidth: NARROW_MAX_WIDTH,
  alignSelf: 'center',
};

// === Auth Context ===
export const AuthContext = React.createContext({
  signIn: async (_token, _user) => {},
  signOut: async () => {},
});

const Stack = createStackNavigator();

function AuthStack() {
  return (
    <Stack.Navigator initialRouteName="Login" screenOptions={{ cardStyle: authCardStyle }}>
      <Stack.Screen
        name="Login"
        component={LoginScreen}
        options={{ headerShown: false }}
      />
    </Stack.Navigator>
  );
}

function AppStack({ token, user }) {
  const commonParams = { token, user };
  const role = String(user?.role || '').toLowerCase();
  const initialRouteName =
    role === 'admin' ? 'AdminHome' : (role === 'worker' || role === 'karyawan' ? 'Monitoring' : 'Scanner');
  const { isPhoneLike, maxWidth } = useResponsive();
  // Stack.Navigator's screenOptions must keep a stable identity across
  // AppStack re-renders. A fresh object every render (e.g. right after a
  // setOptions() call from a screen) made the navigator re-init the active
  // screen, which called setOptions() again -> infinite remount loop that
  // crashed the app right after login (until a page refresh re-mounted
  // everything cleanly).
  const screenOptions = useMemo(
    () => ({
      headerStyle: { backgroundColor: PADIPRO.surface },
      headerTintColor: PADIPRO.primary,
      headerTitleAlign: isPhoneLike ? 'center' : 'left',
      headerBackTitleVisible: false,
      headerTitleStyle: {
        fontWeight: '800',
        color: PADIPRO.textStrong,
        fontSize: isPhoneLike ? 16 : 18,
      },
      cardStyle: {
        flex: 1,
        backgroundColor: PADIPRO.bg,
        width: '100%',
        maxWidth,
        alignSelf: 'center',
      },
    }),
    [isPhoneLike, maxWidth]
  );

  return (
    <Stack.Navigator
      initialRouteName={initialRouteName}
      screenOptions={screenOptions}
    >
      <Stack.Screen
        name="AdminHome"
        component={DashboardScreen}
        initialParams={commonParams}
        options={{ title: 'Dashboard' }}
      />

      {/* Operasional */}
      <Stack.Screen
        name="Operations"
        component={OperationsScreen}
        initialParams={commonParams}
        options={{ title: 'Operasional' }}
      />
      <Stack.Screen
        name="Monitoring"
        component={MonitoringScreen}
        initialParams={commonParams}
        options={{ title: 'Monitoring Sawah' }}
      />
      <Stack.Screen
        name="BukuSawah"
        component={BukuSawahScreen}
        initialParams={commonParams}
        options={{ title: 'Buku Sawah' }}
      />
      <Stack.Screen
        name="TractorDashboard"
        component={TractorDashboardScreen}
        initialParams={commonParams}
        options={{ title: 'Usaha Traktor' }}
      />
      <Stack.Screen
        name="LandMeasure"
        component={LandMeasureScreen}
        initialParams={commonParams}
        options={{ title: 'Ukur Luas Tanah' }}
      />
      {/* Hasil */}
      <Stack.Screen
        name="Harvests"
        component={HarvestsScreen}
        initialParams={commonParams}
        options={{ title: 'Hasil Panen' }}
      />

      {/* Report */}
      <Stack.Screen
        name="Report"
        component={ReportScreen}
        initialParams={commonParams}
        options={{ title: 'Report' }}
      />

      {/* Inventori */}
      <Stack.Screen
        name="Inventory"
        component={InventoryScreen}
        initialParams={commonParams}
        options={{ title: 'Inventori' }}
      />
      <Stack.Screen
        name="PesticideList"
        component={PesticideList}
        initialParams={commonParams}
        options={{ title: 'List Inventori' }}
      />
      <Stack.Screen
        name="History"
        component={HistoryScreen}
        initialParams={commonParams}
        options={{ title: 'History Inventori' }}
      />
      <Stack.Screen
        name="AddPesticide"
        component={AddPesticide}
        initialParams={commonParams}
        options={{ title: 'Tambah Stok' }}
      />

      {/* Expenses */}
      <Stack.Screen
        name="ExpensesList"
        component={ExpensesList}
        initialParams={commonParams}
        options={{ title: 'Expenses' }}
      />
      <Stack.Screen
        name="AddExpense"
        component={AddExpense}
        initialParams={commonParams}
        options={{ title: 'Add Expense' }}
      />
      <Stack.Screen
        name="ReceiptScan"
        component={ReceiptScanScreen}
        initialParams={commonParams}
        options={{ title: 'Scan Struk' }}
      />

      {/* Scanner */}
      <Stack.Screen
        name="Scanner"
        component={ScannerScreen}
        initialParams={commonParams}
      />
    </Stack.Navigator>
  );
}

export default function App() {
  const [ready, setReady] = useState(false);
  const [token, setToken] = useState(null);
  const [user, setUser] = useState(null);

  useEffect(() => {
    if (Platform.OS !== 'web' || typeof document === 'undefined') return undefined;

    let viewport = document.querySelector('meta[name="viewport"]');
    if (!viewport) {
      viewport = document.createElement('meta');
      viewport.setAttribute('name', 'viewport');
      document.head.appendChild(viewport);
    }
    const prevViewport = viewport.getAttribute('content');
    const prevBodyOverflowX = document.body.style.overflowX;
    const prevHtmlOverflowX = document.documentElement.style.overflowX;

    viewport.setAttribute('content', 'width=device-width, initial-scale=1, viewport-fit=cover');
    document.body.style.overflowX = 'hidden';
    document.documentElement.style.overflowX = 'hidden';

    return () => {
      if (prevViewport == null) viewport.removeAttribute('content');
      else viewport.setAttribute('content', prevViewport);
      document.body.style.overflowX = prevBodyOverflowX;
      document.documentElement.style.overflowX = prevHtmlOverflowX;
    };
  }, []);

  // Load session on app start
  useEffect(() => {
    (async () => {
      try {
        const t = await authStorage.get('auth.token');
        const u = await authStorage.get('auth.user');

        if (t && u) {
          setToken(t);
          try { setUser(JSON.parse(u)); } catch { setUser(null); }
        }
      } finally {
        setReady(true);
      }
    })();
  }, []);

  // Auth actions via context
  const authActions = useMemo(
    () => ({
      signIn: async (t, u) => {
        await authStorage.set('auth.token', t);
        await authStorage.set('auth.user', JSON.stringify(u));
        setToken(t);
        setUser(u);
      },
      signOut: async () => {
        try {
          await authStorage.remove('auth.token');
          await authStorage.remove('auth.user');
        } finally {
          setToken(null);
          setUser(null);
        }
      },
    }),
    []
  );

  if (!ready) {
    return (
      <View style={{ flex: 1, alignItems: 'center', justifyContent: 'center' }}>
        <ActivityIndicator />
      </View>
    );
  }

  return (
    <SafeAreaProvider>
      <AuthContext.Provider value={authActions}>
        <NavigationContainer>
          {token && user ? (
            <AppStack key="app" token={token} user={user} />
          ) : (
            <AuthStack key="auth" />
          )}
        </NavigationContainer>
      </AuthContext.Provider>
    </SafeAreaProvider>
  );
}
